import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom';   // <-- correct import

import { useDropzone } from 'react-dropzone'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUpload,
  faFilePdf,
  faFileWord,
  faRobot,
  faLayerGroup,
  faTimes,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faMagic
} from '@fortawesome/free-solid-svg-icons'
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api'
import AutomatedEvaluation from './AutomatedEvaluation'
import BulkAutoDetectUpload from '../components/BulkAutoDetectUpload'
import './UploadQuestionPapers.css'

// Upper bound on papers per bulk batch. Each paper is partitioned and scored
// individually with no manual step, so this is a sanity cap on how much work one
// request commits the server to, not a technical limit.
const BULK_MAX_FILES = 50

// Bulk mode also accepts a single .zip of papers. The archive is expanded
// server-side (backend/services/zip_extractor.py) and the papers it yields go
// through the identical per-paper pipeline as dragged-in files. The same
// BULK_MAX_FILES cap applies to a zip's contents, enforced server-side where the
// real count is known.
const isZipFile = (file) => (file?.name || '').toLowerCase().endsWith('.zip')

const UploadQuestionPapers = () => {
  // Which half of this page is showing. Automated Evaluation used to be its own
  // route; it is a tab here because both halves have exactly one audience -
  // Data Operator and Admin - so navigating between them was navigating between
  // two halves of the same job. They are tabs rather than one long scroll
  // because each is a full workflow with its own course/year/semester fields
  // that mean different things: metadata to file an upload under on one side, a
  // filter to search by on the other. Stacked, those read as one form.
  const [activeTab, setActiveTab] = useState('upload')
  // Both panels stay mounted once they have been shown, and are hidden rather
  // than unmounted when the other tab is active. Unmounting would throw away
  // whatever the user had in progress - a half-filled upload form and its
  // selected files, or a set of search filters and their results - every time
  // someone glanced at the other tab. Navigating between two pages used to cost
  // the same, but a tab invites a casual click in a way a page never did.
  //
  // The evaluation panel is mounted only on first open, not upfront: mounting it
  // fires a ~2 MB course-data fetch, which is wasted on anyone who only came
  // here to upload.
  const [evaluateOpened, setEvaluateOpened] = useState(false)
  // Third tab, same lazy-mount reasoning as evaluateOpened above: it fetches
  // its own course list and session identity on mount, wasted on anyone who
  // did not open it.
  const [autoDetectOpened, setAutoDetectOpened] = useState(false)

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    courseId: '',
    courseName:'',
    instructorName: '',
    instructorEmail: '',
    academicYear: '',
    semester: '',
    examType: '',
    openClosedBook: '',
    programCommittee: '',
    uploaderName: "",
    uploaderEmail: ""
  })

  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploadType, setUploadType] = useState('paper') // 'paper' or 'handout'
  // Bulk mode: one course/period/exam form covers a whole batch of papers.
  // Everything downstream (partitioner, Partition Review stepper) is unchanged;
  // only the step after "Finish Review" differs. See BULK_MAX_FILES below.
  const [bulkMode, setBulkMode] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingProgress, setProcessingProgress] = useState(0)
  const [processingStatus, setProcessingStatus] = useState('')
  const [isCompleted, setIsCompleted] = useState(false)
  const [error, setError] = useState('')
  const [uploadResults, setUploadResults] = useState(null)
  // Set only when a zip upload skipped entries. Holds the redirect so the user
  // reads the skip report instead of being auto-navigated past it.
  const [zipReport, setZipReport] = useState(null)
  const pendingNavigateRef = useRef(null)
  // Terminal state for a question-paper upload. There is no next screen to send
  // the uploader to - partitioning and scoring finish on their own - so the flow
  // ends on a panel pointing at Review Papers.
  const [showUploadComplete, setShowUploadComplete] = useState(false)
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(''); // Track selected course JSON string
  const [courseSearchTerm, setCourseSearchTerm] = useState(''); // Track search input
  const [showCourseDropdown, setShowCourseDropdown] = useState(false); // Control dropdown visibility
  const [filteredCourses, setFilteredCourses] = useState([]); // Filtered courses based on search
  const [customFields, setCustomFields] = useState([]); // Dynamic custom fields
  const [customFieldValues, setCustomFieldValues] = useState({}); // Values for custom fields
  const [userData, setUserData] = useState({});
  const [duplicateCheckInProgress, setDuplicateCheckInProgress] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [confirmingReplacement, setConfirmingReplacement] = useState(false);

  // Academic years for dropdown (last 10 years)
  const academicYears = []
  const currentYear = new Date().getFullYear()
  for (let i = 0; i < 10; i++) {
    const year = currentYear - i
    academicYears.push(`${year}-${(year + 1).toString().slice(-2)}`)
  }

  // How many files the picker accepts: 1 handout, 2 papers normally, or a whole
  // batch in bulk mode.
  const getMaxFiles = () => {
    if (uploadType === 'handout') return 1
    return bulkMode ? BULK_MAX_FILES : 2
  }

  // Fetch SAML user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`/qpra/get-saml-data`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.log('Session missing/expired — redirecting to login');
            window.location.href = `/qprs/login`;
            return;
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setUserData(data);
        
        // Auto-populate uploader fields from SAML data
        setFormData((prev) => ({
          ...prev,
          uploaderName: data.name || '',
          uploaderEmail: data.email || ''
        }));

      } catch (err) {
        console.error('Error fetching SAML user data:', err);
        setError(err.message || 'Failed to load user data');
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ALL_COURSES);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch courses");

        const data = await response.json();
        setAvailableCourses(data.courses || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching courses:", err);
        setError(err.message || "Failed to load courses");
        setAvailableCourses([]);
      }
    };

    fetchCourses();
  }, []);

  // Fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CUSTOM_FIELDS);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch custom fields");

        const data = await response.json();
        // Handle both array and object responses
        const fieldsArray = Array.isArray(data) ? data : (data.fields || []);
        // Filter to only active fields, sorted by display_order
        const activeFields = fieldsArray
          .filter(field => field.is_active)
          .sort((a, b) => a.display_order - b.display_order);
        
        setCustomFields(activeFields);
        
        // Initialize custom field values (empty strings for new upload)
        const initialValues = {};
        activeFields.forEach(field => {
          initialValues[field.field_name] = '';
        });
        setCustomFieldValues(initialValues);
      } catch (err) {
        console.error("Error fetching custom fields:", err);
        // Don't set error state - custom fields are optional
        setCustomFields([]);
        setCustomFieldValues({});
      }
    };

    fetchCustomFields();
  }, []);



  const onDrop = useCallback((acceptedFiles) => {
    // A .zip stands in for a whole batch, so it is accepted on its own only:
    // mixing an archive with loose files makes the effective paper count
    // unknowable client-side, and the server rejects that combination anyway.
    const incomingZips = acceptedFiles.filter(isZipFile)
    if (incomingZips.length > 0) {
      if (incomingZips.length > 1 || acceptedFiles.length > 1 || uploadedFiles.length > 0) {
        setError('Upload a .zip on its own — it cannot be combined with other files.')
        return
      }
      // The archive's real contents are only known after server-side
      // extraction, so BULK_MAX_FILES is enforced there, not here.
      setUploadedFiles([{
        file: incomingZips[0],
        id: Math.random().toString(36).substr(2, 9),
        name: incomingZips[0].name,
        size: incomingZips[0].size,
        status: 'pending'
      }])
      setError('')
      return
    }

    // Adding loose files to an already-selected zip is the same conflict.
    if (uploadedFiles.some(f => isZipFile(f))) {
      setError('Upload a .zip on its own — remove it first to add individual files.')
      return
    }

    // Enforce file count based on upload type
    const maxFiles = getMaxFiles()
    if (uploadedFiles.length + acceptedFiles.length > maxFiles) {
      setError(
        uploadType === 'handout'
          ? 'Only one handout file allowed.'
          : bulkMode
          ? `Maximum ${BULK_MAX_FILES} question papers per bulk batch. Please remove some files first.`
          : 'Maximum 2 question papers allowed. Please remove some files first.'
      )
      return
    }

    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      status: 'pending'
    }))
    
    setUploadedFiles(prev => [...prev, ...newFiles])
    setError('')
    // getMaxFiles is un-memoized but its entire output is a pure function of
    // uploadType and bulkMode, both already listed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles, uploadType, bulkMode])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      // Zip is offered only in bulk mode; the single/2-paper path is unchanged.
      ...(bulkMode && uploadType !== 'handout'
        ? { 'application/zip': ['.zip'], 'application/x-zip-compressed': ['.zip'] }
        : {})
    },
    multiple: uploadType !== 'handout',
    maxFiles: getMaxFiles(),
    disabled: uploadedFiles.length >= getMaxFiles()
  })

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
  }

// ✅ Modified handleInputChange
const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: value
  }));
};

// Handle custom field changes
const handleCustomFieldChange = (fieldName, value) => {
  setCustomFieldValues((prev) => ({
    ...prev,
    [fieldName]: value
  }));
};

// Handle course search input
const handleCourseSearch = (e) => {
  const searchValue = e.target.value;
  setCourseSearchTerm(searchValue);
  setShowCourseDropdown(true);
  
  // Filter courses based on search term
  if (searchValue.trim() === '') {
    setFilteredCourses(availableCourses);
  } else {
    const filtered = availableCourses.filter(course => {
      const searchLower = searchValue.toLowerCase();
      return (
        course.course_code.toLowerCase().includes(searchLower) ||
        course.course_title.toLowerCase().includes(searchLower)
      );
    });
    setFilteredCourses(filtered);
  }
};

// Handle course selection from dropdown
const handleCourseSelect = (course) => {
  setSelectedCourse(JSON.stringify(course));
  setCourseSearchTerm(`${course.course_code} - ${course.course_title}`);
  setFormData((prev) => ({
    ...prev,
    courseName: course.course_title,
    courseId: course.course_code,
    instructorEmail: course.faculty_email_id || course.faculty_email || course.instructor_email || prev.instructorEmail || ''
  }));
  setShowCourseDropdown(false);
};

// Handle focus on search input
const handleCourseFocus = () => {
  setShowCourseDropdown(true);
  setFilteredCourses(availableCourses);
};


  const validateForm = () => {
    // Every one of these is required, not advisory. A paper uploaded here has no
    // QPM catalogue record behind it, so metadata missing at upload time is the
    // only metadata it will ever have - and a paper with none cannot be found in
    // Review Papers afterwards. The backend rejects the same set independently;
    // this exists to name the offending field before a file is sent.
    if (!formData.courseId.trim()) {
      setError('Course Code is required')
      return false
    }
    if (!formData.courseName.trim()) {
      setError('Course Name is required')
      return false
    }
    if (!formData.academicYear) {
      setError('Academic Year is required')
      return false
    }
    if (!formData.semester) {
      setError('Semester is required')
      return false
    }
    if (!formData.examType) {
      setError('Exam Type is required')
      return false
    }
    if (!formData.openClosedBook) {
      setError('Open/Closed Book selection is required')
      return false
    }
    if (!formData.programCommittee) {
      setError('Program Committee is required')
      return false
    }
    if (!formData.instructorEmail.trim()) {
      setError('Instructor Email is required')
      return false
    }
    if (!formData.uploaderName.trim()) {
      setError('Uploader Name is required');
      return false;
    }
    if (!formData.uploaderEmail.trim()) {
      setError('Uploader Email is required');
      return false;
    }
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one file');
      return false;
    }
    
    // Validate required custom fields
    for (const field of customFields) {
      if (field.is_required && !customFieldValues[field.field_name]?.trim()) {
        setError(`${field.field_label} is required`);
        return false;
      }
    }
    
    return true
  }

  const checkForDuplicates = async () => {
    if (uploadType !== 'paper') {
      // No duplicate check for handouts
      return true;
    }

    setDuplicateCheckInProgress(true);
    
    try {
      const params = new URLSearchParams({
        course_id: formData.courseId,
        exam_type: formData.examType,
        semester: formData.semester,
        academic_year: formData.academicYear
      });

      const response = await fetch(`/upload/check-duplicate?${params}`);
      
      if (!response.ok) {
        console.error('Error checking for duplicates: HTTP', response.status);
        return true; // Proceed with upload if check fails
      }

      const result = await response.json();
      
      if (result.has_duplicates) {
        setDuplicates(result.duplicates);
        setShowDuplicateModal(true);
        return false; // Don't proceed with upload yet
      }
      
      return true; // No duplicates, proceed with upload
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return true; // Proceed with upload if check fails
    } finally {
      setDuplicateCheckInProgress(false);
    }
  };

  const handleConfirmReplacement = async () => {
    setConfirmingReplacement(true);
    setShowDuplicateModal(false);
    
    try {
      await performUpload(true); // true = replace existing
    } finally {
      setConfirmingReplacement(false);
    }
  };

  const handleCancelReplacement = () => {
    setShowDuplicateModal(false);
    setDuplicates([]);
  };

  // Return the form to a state where another batch can be uploaded. Shared by
  // the handout path (which resets itself after a moment) and by "Upload more
  // papers" on the completion panel.
  const resetUploadForm = () => {
    setUploadedFiles([])
    setFormData({
      courseId: '',
      courseName: '',
      instructorName: '',
      instructorEmail: '',
      academicYear: '',
      semester: '',
      examType: '',
      openClosedBook: '',
      programCommittee: '',
      uploaderName: "",
      uploaderEmail: ""
    })
    setCourseSearchTerm('')
    const resetValues = {}
    customFields.forEach(field => {
      resetValues[field.field_name] = ''
    })
    setCustomFieldValues(resetValues)

    setIsProcessing(false)
    setProcessingProgress(0)
    setProcessingStatus('')
    setIsCompleted(false)
    setUploadResults(null)
  }

  const performUpload = async (shouldReplace = false) => {
    setIsProcessing(true)
    setProcessingProgress(0)
    setProcessingStatus('Preparing files for upload...')
    setError('')

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData()
      formDataToSend.append('course_id', formData.courseId)
      formDataToSend.append('course_name', formData.courseName)
      formDataToSend.append('instructor_name', formData.instructorEmail  || '')
      formDataToSend.append('academic_year', formData.academicYear)
      formDataToSend.append('semester', formData.semester)

      // NEW FIELDS appended to payload
      formDataToSend.append('exam_type', formData.examType || '')
      formDataToSend.append('open_closed_book', formData.openClosedBook || '')
      formDataToSend.append('program_committee', formData.programCommittee || '')
      formDataToSend.append('instructor_email', formData.instructorEmail || '')
      formDataToSend.append('uploader_name', formData.uploaderName || '');
      formDataToSend.append('uploader_email', formData.uploaderEmail || '');
      formDataToSend.append('replace_existing', shouldReplace ? 'true' : 'false');
      // Bulk provenance now travels with the request. It used to be stashed in
      // sessionStorage for Partition Review to read; with that screen gone the
      // backend dispatches scoring itself and needs to know which engine this
      // batch belongs to.
      formDataToSend.append('bulk_mode', bulkMode ? 'true' : 'false');

      // Add custom fields as JSON (includes system fields: program_name, program_category, program_committee, program_code)
      formDataToSend.append('custom_fields', JSON.stringify(customFieldValues));

      // Add files to FormData
      uploadedFiles.forEach((fileObj) => {
        formDataToSend.append('files', fileObj.file)
      })
      formDataToSend.append('upload_type', uploadType)

      // Simulate processing progress
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return 90
          }
          return prev + 10
        })
      }, 500)

      // Make API call to backend
      const response = await fetch('/upload/upload-question-papers', {
        method: 'POST',
        body: formDataToSend
      })

      clearInterval(progressInterval)

      if (!response.ok) {
        // response.statusText is always "" over HTTP/2 - the reason phrase was
        // removed from the protocol - and the deployed stack serves HTTP/2. A
        // message built from it reads "Upload failed:" and withholds the only
        // two things worth knowing. Report the status code, and the `detail`
        // the backend actually sends, falling back to raw text for a proxy
        // error (502/504) whose body is HTML rather than JSON.
        let detail = ''
        try {
          const body = await response.clone().json()
          detail = body?.detail || body?.error || ''
        } catch {
          const text = await response.text().catch(() => '')
          detail = text.trim().slice(0, 300)
        }
        throw new Error(
          `Upload failed (HTTP ${response.status})${detail ? `: ${detail}` : ''}`)
      }

      const result = await response.json()
      
      setProcessingProgress(100)
      setProcessingStatus(uploadType === 'handout'
        ? 'Handout processed successfully!'
        : 'Uploaded. Partitioning and scoring are running automatically.')
      setIsCompleted(true)
      setUploadResults(result)
      
      // Redirect based on upload type
      if (uploadType === 'handout') {
        // For handouts, show success message and stay on upload page
        setTimeout(resetUploadForm, 2000)
      } else {
        // Question papers: nothing further for the user to do here. Partitioning
        // and scoring both run server-side without a gate, so this stops at a
        // message pointing at Review Papers instead of walking the uploader
        // through Partition Review's per-question stepper.
        const finishUpload = () => {
          setIsProcessing(false)
          setShowUploadComplete(true)
        }

        // If the archive dropped anything, show what was skipped first. Skipping
        // straight past this is exactly the silent-drop behaviour the zip report
        // exists to prevent.
        const zipInfo = result?.zip_extraction
        if (zipInfo && (zipInfo.skipped_count > 0 || zipInfo.renamed_count > 0)) {
          pendingNavigateRef.current = finishUpload
          setZipReport(zipInfo)
          setIsProcessing(false)
        } else {
          setTimeout(finishUpload, 1200)
        }
      }

    } catch (err) {
      setError(err.message || 'An error occurred during processing')
      setIsProcessing(false)
      setProcessingProgress(0)
      setProcessingStatus('')
      setIsCompleted(false)
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // Check for duplicates before uploading (only for papers, not handouts)
    if (uploadType === 'paper') {
      setDuplicateCheckInProgress(true);
      const canProceed = await checkForDuplicates();
      setDuplicateCheckInProgress(false);
      
      if (!canProceed) {
        // Modal will be shown by checkForDuplicates
        return;
      }
    }

    // No duplicates or user confirmed replacement, proceed with upload
    await performUpload(false);
  }


  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="upload-question-papers">
      <div className="container">
        <div className="page-header">
          <div className="header-icon">
            <FontAwesomeIcon icon={
              activeTab === 'upload' ? faUpload
              : activeTab === 'evaluate' ? faRobot
              : faMagic
            } />
          </div>
          <div className="header-text">
            <h1>
              {activeTab === 'upload' ? 'Upload Question Papers'
                : activeTab === 'evaluate' ? 'Automated Evaluation'
                : 'Bulk Upload (Auto-Detect Course)'}
            </h1>
            <p>
              {activeTab === 'upload'
                ? 'Upload question papers for automatic partitioning and processing'
                : activeTab === 'evaluate'
                ? 'Find already-evaluated papers, and evaluate the ones still pending'
                : 'Drop files from many courses at once - the course is read off each cover'}
            </p>
          </div>
        </div>

        {/* The "Automated Evaluation →" button that used to sit in the header
            navigated to a separate page. It switches tabs now. */}
        <div className="uqp-tabs" role="tablist" aria-label="Upload Question Papers sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'upload'}
            className={`uqp-tab ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <FontAwesomeIcon icon={faUpload} />
            Upload Files
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'evaluate'}
            className={`uqp-tab ${activeTab === 'evaluate' ? 'active' : ''}`}
            onClick={() => { setEvaluateOpened(true); setActiveTab('evaluate') }}
          >
            <FontAwesomeIcon icon={faRobot} />
            Automated Evaluation
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'autodetect'}
            className={`uqp-tab ${activeTab === 'autodetect' ? 'active' : ''}`}
            onClick={() => { setAutoDetectOpened(true); setActiveTab('autodetect') }}
          >
            <FontAwesomeIcon icon={faMagic} />
            Bulk Auto-Detect
          </button>
        </div>

        {/* Duplicate Paper Modal */}
        {showDuplicateModal && duplicates.length > 0 && (
          <div className="modal-overlay" onClick={handleCancelReplacement}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Duplicate Question Paper</h2>
                <button 
                  type="button" 
                  className="modal-close"
                  onClick={handleCancelReplacement}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className="modal-body">
                <div className="duplicate-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <p>A question paper with the same course, exam type, semester, and academic year has already been uploaded.</p>
                </div>
                
                <div className="duplicate-papers-list">
                  <h3>Existing Papers:</h3>
                  {duplicates.map((paper, index) => (
                    <div key={index} className="duplicate-paper-item">
                      <div className="paper-info">
                        <p><strong>File:</strong> {paper.original_filename}</p>
                        <p><strong>Course:</strong> {paper.course_id} - {paper.course_name}</p>
                        <p><strong>Exam Type:</strong> {paper.exam_type}</p>
                        <p><strong>Uploaded by:</strong> {paper.uploader_name} ({paper.uploader_email})</p>
                        <p><strong>Uploaded on:</strong> {new Date(paper.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn-cancel"
                  onClick={handleCancelReplacement}
                  disabled={confirmingReplacement}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn-replace"
                  onClick={handleConfirmReplacement}
                  disabled={confirmingReplacement}
                >
                  {confirmingReplacement ? 'Replacing...' : 'Replace'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="upload-form-container"
             style={{ display: activeTab === 'upload' ? undefined : 'none' }}>
          <form onSubmit={handleSubmit} className="upload-form">
            <h3>Course Information</h3>
            <div className="form-container">

 
    {/* Basic Course Information Section */}
    <div className="form-section-2">
      <div className="section-header-upload">
        <h3 className="section-title">
          <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
          </svg>
          Basic Course Information
        </h3>
      </div>
      <div className="section-content">
        <div className="form-grid-2">
<div className="form-group" style={{ position: 'relative' }}>
  <label htmlFor="courseName" className="required">Course Name</label>
  <div className="dropdown-container">
    <input
      type="text"
      id="courseName"
      name="courseName"
      value={courseSearchTerm}
      onChange={handleCourseSearch}
      onFocus={handleCourseFocus}
      onBlur={() => setTimeout(() => setShowCourseDropdown(false), 200)}
      className="input-field"
      placeholder="Search by course code or name..."
      autoComplete="off"
      required
    />
    {showCourseDropdown && filteredCourses.length > 0 && (
      <div className="course-dropdown-list" style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        maxHeight: '300px',
        overflowY: 'auto',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        zIndex: 1000,
        marginTop: '4px'
      }}>
        {filteredCourses.map((course, index) => (
          <div
            key={index}
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent input blur
              handleCourseSelect(course);
            }}
            style={{
              padding: '10px 15px',
              cursor: 'pointer',
              borderBottom: index < filteredCourses.length - 1 ? '1px solid #f0f0f0' : 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            <div style={{ fontWeight: '600', color: '#333' }}>{course.course_code}</div>
            <div style={{ fontSize: '0.9em', color: '#666' }}>{course.course_title}</div>
          </div>
        ))}
      </div>
    )}
    {showCourseDropdown && filteredCourses.length === 0 && courseSearchTerm && (
      <div className="course-dropdown-list" style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        zIndex: 1000,
        marginTop: '4px',
        padding: '15px',
        textAlign: 'center',
        color: '#999'
      }}>
        No courses found matching "{courseSearchTerm}"
      </div>
    )}
  </div>
</div>

<div className="form-group">
  <label htmlFor="courseId" className="required">Course Code</label>
  <input
    type="text"
    id="courseId"
    name="courseId"
    value={formData.courseId}
    className="input-field"
    readOnly
    placeholder="Course code will appear here"
  />
</div>

       
          

        </div>
      </div>
    </div>
    <div className="two-column-layout">

    {/* Academic Period Section */}
    <div className="form-section-2">
      <div className="section-header-upload">
        <h3 className="section-title">
          <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          Academic Period
        </h3>
      </div>
      <div className="section-content">
        <div className="form-grid-2">
          <div className="form-group">
            <label htmlFor="academicYear" className="required">Academic Year</label>
            <select
              id="academicYear"
              name="academicYear"
              value={formData.academicYear}
              onChange={handleInputChange}
              required
              disabled={isProcessing}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="semester" className="required">Semester</label>
            <select
              id="semester"
              name="semester"
              value={formData.semester}
              onChange={handleInputChange}
              required
              disabled={isProcessing}
            >
              <option value="">Select Semester</option>
              <option value="1">Semester 1</option>
              <option value="2">Semester 2</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  {/* Exam Configuration Section */}
  <div className="form-section-2">
    <div className="section-header-upload">
      <h3 className="section-title">
        <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
        </svg>
        Exam Configuration
      </h3>
    </div>
    <div className="section-content">
      <div className="form-grid-2">
        <div className="form-group">
          <label htmlFor="examType" className="required">Exam Type</label>
          <select
            id="examType"
            name="examType"
            value={formData.examType}
            onChange={handleInputChange}
            required
            disabled={isProcessing}
          >
            <option value="">Select Exam Type</option>
            <option value="ec1-regular">EC1 - Regular</option>
            <option value="ec2-regular">EC2 - Regular</option>
            <option value="ec3-regular">EC3 - Regular</option>
            <option value="ec1-makeup">EC1 - Makeup</option>
            <option value="ec2-makeup">EC2 - Makeup</option>
            <option value="ec3-makeup">EC3 - Makeup</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="openClosedBook" className="required">Exam Format</label>
          <select
            id="openClosedBook"
            name="openClosedBook"
            value={formData.openClosedBook}
            onChange={handleInputChange}
            required
            disabled={isProcessing}
          >
            <option value="">Select Format</option>
            <option value="open">Open Book</option>
            <option value="closed">Closed Book</option>
          </select>
        </div>




      </div>
    </div>
  </div>
</div>          

      {/* Program Classification Section */}
  <div className="form-section-2">
    <div className="section-header-upload">
      <h3 className="section-title">
        <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
        Program Classification
      </h3>
    </div>
    <div className="section-content">
      <div className="form-grid-3">
        {/* All Custom Fields - Only select dropdowns with options */}
        {customFields
          .filter(field => field.field_type === 'select' && field.options && field.options.length > 0)
          .map((field) => (
          <div key={field.field_name} className="form-group small-form-group">
            <label htmlFor={field.field_name} className={field.is_required ? 'required' : ''}>
              {field.field_label}
            </label>
            <select
              id={field.field_name}
              name={field.field_name}
              value={customFieldValues[field.field_name] || ''}
              onChange={(e) => handleCustomFieldChange(field.field_name, e.target.value)}
              required={field.is_required}
              disabled={isProcessing}
            >
              <option value="">Select {field.field_label}</option>
              {field.options.map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}

      </div>
    </div>
            <div className="form-group">
          <label htmlFor="programCommittee" className="required">Program Committee</label>
          <select
            id="programCommittee"
            name="programCommittee"
            value={formData.programCommittee}
            onChange={handleInputChange}
            required
            disabled={isProcessing}
          >
            <option value="">Select Program Committee</option>
            <option value="CSIS">CSIS</option>
            <option value="Core Engg.">Core Engg.</option>
            <option value="Management">Management</option>
            <option value="EEE">EEE</option>
          </select>
        </div>
  </div>

  <div className="form-section">
    <h3>Upload Files</h3>
    
    {/* Uploader Fields */}
    <div className="form-grid-3">
      <div className="form-group small-form-group">
        <label htmlFor="instructorEmail" className="required">Instructor Email ID</label>
        <input
          type="email"
          id="instructorEmail"
          name="instructorEmail"
          value={formData.instructorEmail}
          onChange={handleInputChange}
          required
          disabled={isProcessing}
          placeholder="Enter instructor email"
        />
      </div>

      <div className="form-group small-form-group">
        <label htmlFor="uploaderName" className="required">Uploader Name</label>
        <input
          type="text"
          id="uploaderName"
          name="uploaderName"
          value={formData.uploaderName}
          onChange={handleInputChange}
          required
          disabled={true}
          style={{  cursor: 'not-allowed' }}
        />
      </div>

      <div className="form-group small-form-group">
        <label htmlFor="uploaderEmail" className="required">Uploader Email ID</label>
        <input
          type="email"
          id="uploaderEmail"
          name="uploaderEmail"
          value={formData.uploaderEmail}
          onChange={handleInputChange}
          required
          disabled={true}
          style={{ cursor: 'not-allowed' }}
        
        />
      </div>
    </div>
    
    <div className="form-group">
      <label>Upload Type</label>
      <select value={uploadType} onChange={(e)=>{setUploadType(e.target.value); setUploadedFiles([])}} disabled={isProcessing}>
        <option value="paper">Question Paper(s)</option>
        <option value="handout">Course Handout</option>
      </select>
    </div>
    <div 
                {...getRootProps()} 
                className={`dropzone ${isDragActive ? 'active' : ''} ${isProcessing ? 'disabled' : ''} ${uploadedFiles.length >= getMaxFiles() ? 'max-files' : ''}`}
              >
                <input {...getInputProps()} />
                <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                <p className="dropzone-text">
                  {uploadedFiles.length >= getMaxFiles()
                    ? `Maximum files reached (${uploadedFiles.length}/${getMaxFiles()})`
                    : isDragActive
                    ? 'Drop the files here...'
                    : (uploadType==='handout'
                        ? 'Drag & drop one PDF/DOCX handout here, or click to select file'
                        : bulkMode
                        ? 'Drag & drop the whole batch of PDF/DOCX papers — or a single .zip of them — here, or click to select'
                        : 'Drag & drop PDF or DOCX files here, or click to select files')
                  }
                </p>
                <p className="dropzone-hint">
                  {uploadType==='handout'
                    ? `Supports PDF/DOCX ( ${uploadedFiles.length}/1 file )`
                    : bulkMode
                      ? `Supports PDF, DOCX, or one .zip (${uploadedFiles.length}/${getMaxFiles()} files)`
                      : `Supports PDF and DOCX files (${uploadedFiles.length}/${getMaxFiles()} files)`}
                </p>
                {bulkMode && uploadType !== 'handout' && (
                  <p className="dropzone-hint bulk-hint">
                    Bulk mode: the course details above apply to every paper in this batch.
                    Each paper is still partitioned and reviewed individually.
                    A .zip must be uploaded on its own and may contain at most {BULK_MAX_FILES} papers;
                    non-PDF/DOCX entries are skipped and listed for you.
                  </p>
                )}
                {uploadedFiles.length >= getMaxFiles() && (
                  <p className="max-files-notice">
                    Remove a file to upload more
                  </p>
                )}
              </div>

              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  <h4>Selected Files ({uploadedFiles.length})</h4>
                  <div className="file-list">
                    {uploadedFiles.map((fileObj) => (
                      <div key={fileObj.id} className="file-item">
                        <FontAwesomeIcon 
                          icon={fileObj.name.toLowerCase().endsWith('.docx') ? faFileWord : faFilePdf} 
                          className="file-icon" 
                        />
                        <div className="file-info">
                          <span className="file-name">{fileObj.name}</span>
                          <span className="file-size">{formatFileSize(fileObj.size)}</span>
                        </div>
                        {!isProcessing && (
                          <button
                            type="button"
                            onClick={() => removeFile(fileObj.id)}
                            className="remove-file-btn"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
  </div>

  {error && (
    <div className="error-message">
      <FontAwesomeIcon icon={faExclamationTriangle} />
      <span>{error}</span>
    </div>
  )}

  {showUploadComplete && (
    <div className="upload-complete">
      <div className="upload-complete-header">
        <FontAwesomeIcon icon={faCheckCircle} />
        <span>
          {uploadResults?.paper_ids?.length || 0} paper
          {(uploadResults?.paper_ids?.length || 0) === 1 ? '' : 's'} uploaded
        </span>
      </div>
      <p className="upload-complete-body">
        Partitioning and scoring run automatically - there is no review step
        here. Results appear in Review Papers once each paper finishes, which
        takes a minute or two per paper.
      </p>
      <div className="upload-complete-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/review_page')}
        >
          Go to Review Papers
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setShowUploadComplete(false)
            resetUploadForm()
          }}
        >
          Upload more papers
        </button>
      </div>
    </div>
  )}

  {zipReport && (
    <div className="zip-report">
      <div className="zip-report-header">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <span>
          Extracted {zipReport.extracted_count} paper
          {zipReport.extracted_count === 1 ? '' : 's'} from the zip
          {zipReport.skipped_count > 0 && `, skipped ${zipReport.skipped_count}`}
          {zipReport.renamed_count > 0 && `, renamed ${zipReport.renamed_count}`}
          {' '}(of {zipReport.total_entries} entries).
        </span>
      </div>

      {zipReport.skipped_count > 0 && (
        <>
          <p className="zip-report-subhead">Skipped — not uploaded:</p>
          <ul className="zip-report-list">
            {zipReport.skipped.map((s, i) => (
              <li key={`skip-${i}`}><code>{s.name}</code> — {s.reason}</li>
            ))}
          </ul>
        </>
      )}

      {zipReport.renamed_count > 0 && (
        <>
          <p className="zip-report-subhead">
            Renamed — duplicate filenames inside the archive:
          </p>
          <ul className="zip-report-list">
            {zipReport.renamed.map((r, i) => (
              <li key={`rename-${i}`}>
                <code>{r.original}</code> stored as <code>{r.stored_as}</code>
              </li>
            ))}
          </ul>
        </>
      )}

      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          const go = pendingNavigateRef.current
          setZipReport(null)
          if (go) go()
        }}
      >
        Continue ({zipReport.extracted_count} paper
        {zipReport.extracted_count === 1 ? '' : 's'})
      </button>
    </div>
  )}

  {isProcessing && (
    <div className="processing-status">
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${processingProgress}%` }}
          ></div>
        </div>
        <span className="progress-text">{processingProgress}%</span>
      </div>
      <p className="status-text">{processingStatus}</p>
      {processingProgress < 100 && (
        <div className="spinner">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
    </div>
  )}

  {isCompleted && (
    <div className="success-message">
      <FontAwesomeIcon icon={faCheckCircle} />
      <span>{uploadType === 'handout' ? 'Handout processed successfully!' : 'Question papers processed successfully!'}</span>
    </div>
  )}

  <div className="form-actions">
    <button
      type="submit"
      className="btn btn-primary submit-btn"
      disabled={isProcessing || uploadedFiles.length === 0}
    >
      {isProcessing ? 'Processing...' : (uploadType==='handout' ? 'Upload Handout' : 'Upload & Process Question Papers')}
    </button>

    {uploadType !== 'handout' && (
      <button
        type="button"
        className={`btn bulk-upload-btn ${bulkMode ? 'btn-primary' : 'btn-secondary'}`}
        disabled={isProcessing}
        onClick={() => {
          if (bulkMode) {
            // Leaving bulk mode: drop anything over the single-upload cap.
            setBulkMode(false)
            setUploadedFiles(prev => prev.slice(0, 2))
          } else {
            setBulkMode(true)
          }
          setError('')
        }}
        title="Use one course/period/exam form for a whole batch of papers"
      >
        <FontAwesomeIcon icon={faLayerGroup} />
        {bulkMode ? 'Bulk Upload: On' : 'Bulk Upload'}
      </button>
    )}
  </div>

  {bulkMode && uploadType !== 'handout' && (
    <p className="bulk-mode-note">
      {uploadedFiles.length} paper{uploadedFiles.length === 1 ? '' : 's'} queued.
      Each is partitioned and then scored automatically - same rubric as a single
      upload - and appears in Review Papers when its analysis lands.
    </p>
  )}
</div>
          </form>
        </div>

        {/* The whole of what used to be /automated_evaluation: the course and
            period filters, the evaluated/pending counts, the PDF preview and its
            picker, the pending list with its per-slot "Evaluate now" and pager.
            Nothing was dropped in the move - the component only lost its own
            page header and its back-to-upload button, which the tabs replace. */}
        {evaluateOpened && (
          <div style={{ display: activeTab === 'evaluate' ? undefined : 'none' }}>
            <AutomatedEvaluation />
          </div>
        )}

        {/* Third tab: bulk upload without a course picked per file. A separate
            component entirely - it has its own file list, its own form state,
            and its own endpoints (/upload/auto-detect/scan, .../confirm), and
            touches none of the state above. */}
        {autoDetectOpened && (
          <div style={{ display: activeTab === 'autodetect' ? undefined : 'none' }}>
            <BulkAutoDetectUpload />
          </div>
        )}
      </div>
    </div>
  )
}

export default UploadQuestionPapers
